const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL
  || process.env.REACT_APP_API_URL
  || 'http://localhost:8080/api';
const API_BASE_URL = configuredApiBaseUrl.replace(/\/+$/, '');

let accessToken = null;

const DEFAULT_ERROR_MESSAGES = {
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform that action.',
  404: 'We could not find what you were looking for.',
  500: 'The server ran into a problem. Please try again.',
};

export class ApiError extends Error {
  constructor(message, { status = null, errorCode = null, details = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.details = details;
  }
}

export const setApiAccessToken = (token) => {
  accessToken = token || null;
};

const parseResponseBody = async (response) => {
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text ? { message: text } : null;
};

const normalizeErrorMessage = (status, errorData, statusText) => {
  if (typeof errorData?.message === 'string' && errorData.message.trim()) {
    return errorData.message.trim();
  }

  return DEFAULT_ERROR_MESSAGES[status] || `API request failed with status ${status}: ${statusText}`;
};

const request = async (path, options = {}) => {
  const {
    method = 'GET',
    body,
    headers = {},
    includeCredentials = false,
    requiresAuth = false,
    signal,
  } = options;
  const nextHeaders = new Headers(headers);

  if (!(body instanceof FormData) && body !== undefined && !nextHeaders.has('Content-Type')) {
    nextHeaders.set('Content-Type', 'application/json');
  }

  if (accessToken && !nextHeaders.has('Authorization')) {
    nextHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  if (requiresAuth && !accessToken) {
    throw new ApiError('Please log in to continue.', {
      status: 401,
      errorCode: 'AUTH_REQUIRED',
    });
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: nextHeaders,
    body: body === undefined || body instanceof FormData ? body : JSON.stringify(body),
    credentials: includeCredentials ? 'include' : 'same-origin',
    signal,
  });
  const data = await parseResponseBody(response);

  if (!response.ok) {
    throw new ApiError(normalizeErrorMessage(response.status, data, response.statusText), {
      status: response.status,
      errorCode: data?.errorCode || null,
      details: data,
    });
  }

  return data;
};

export const isAuthenticationError = (error) => (
  error?.status === 401 ||
  error?.status === 403 ||
  error?.errorCode === 'AUTH_REQUIRED'
);

export const eventApi = {
  getEvent: async (eventId) => request(`/event/${eventId}`),
  // getEventsByPromotionId: async (promotionId) => request(`/event/promotion/${promotionId}`),
  getEventsByPromotionId: async (
    promotionId,
    { page = 1, signal } = {}
  ) => {
    const params = new URLSearchParams({
      page: String(page),
    });

    return request(
      `/event/promotion/${promotionId}?${params.toString()}`,
      { signal } 
    );
  },
};

export const fightApi = {
  getFightsByEventId: async (eventId) => request(`/fight/event/${eventId}`),
};

export const fighterApi = {
  getFighterById: async (fighterId) => request(`/fighter/${fighterId}`),
};

export const searchApi = {
  search: async ({ type, query, signal }) => {
    const endpoint = type === 'event' ? 'event' : 'fighter';
    const params = new URLSearchParams({ query });
    const data = await request(`/search/${endpoint}?${params.toString()}`, {
      signal,
    });

    return (data || []).map((item) => ({
      id: item.id,
      name: item.name,
      resultType: item.type,
    }));
  },
};

export const authApi = {
  login: async ({ email, password }) => request('/auth/login', {
    method: 'POST',
    body: { email, password },
    includeCredentials: true,
  }),
  refresh: async () => request('/auth/refresh', {
    method: 'POST',
    includeCredentials: true,
  }),
  register: async ({ token, username, password }) => request('/auth/register', {
    method: 'POST',
    body: { token, username, password },
    includeCredentials: true,
  }),
  logout: async () => request('/auth/logout', {
    method: 'POST',
    includeCredentials: true,
  }),
  createInvite: async ({ email }) => request('/auth/admin/invite', {
    method: 'POST',
    body: { email },
    requiresAuth: true,
  }),
};
